import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBill1,
  faCalendarAlt,
  faLocationDot,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Modal from "../../components/modal/modal";

const VacancyList = ({ fetchUrl, titleKey, itemsPerPage = 20 }) => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [likedVacancies, setLikedVacancies] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const userId = user?.id;

  // 🔹 Получение вакансий
  const fetchVacancies = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${fetchUrl}?page=${pageNumber}&limit=${itemsPerPage}`,
      );
      const data = await res.json();
      if (data.success) {
        setVacancies(data.vacancies);
        setTotalPages(data.totalPages);
        setPage(pageNumber);
      }
    } catch (err) {
      console.error("Ошибка при получении вакансий:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Получение лайков пользователя
  useEffect(() => {
    if (!userId) return;
    const fetchLikes = async () => {
      try {
        const res = await fetch(
          `/api/get_liked_vacancies.php?user_id=${userId}`,
        );
        const data = await res.json();
        setLikedVacancies(
          Array.isArray(data)
            ? data.map((v) => Number(v.id))
            : data.liked?.map((v) => Number(v.id)) || [],
        );
      } catch (err) {
        console.error("Ошибка при получении лайков:", err);
      }
    };
    fetchLikes();
  }, [userId]);

  useEffect(() => {
    fetchVacancies(1);
  }, [fetchUrl]);

  // 🔹 Обработчик лайка
  const handleLike = async (vacancyId) => {
    if (!user) {
      setModalMessage({
        title: t("attention"),
        ok: t("ok"),
        text: t("pleaseregister"),
        success: true,
        actions: [
          {
            label: t("register"),
            onClick: () => {
              navigate("/WorkerChoose");
              setModalMessage(null);
            },
          },
        ],
      });
      localStorage.setItem("pending_like", vacancyId);
      return;
    }

    const alreadyLiked = likedVacancies.includes(Number(vacancyId));

    try {
      const res = await fetch("/api/toggle_like.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          vacancy_id: vacancyId,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setLikedVacancies((prev) =>
          alreadyLiked
            ? prev.filter((id) => id !== Number(vacancyId))
            : [...prev, Number(vacancyId)],
        );
      }
    } catch (err) {
      console.error("Ошибка при лайке:", err);
    }
  };

  // 🔹 Проверка pending_like после регистрации
  useEffect(() => {
    const pending = localStorage.getItem("pending_like");
    if (user && pending) {
      handleLike(Number(pending));
      localStorage.removeItem("pending_like");
    }
  }, [user]);

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center h-[200px]">
        <p className="text-[#006674] font-semibold animate-pulse text-xl">
          {t("loading")}...
        </p>
      </div>
    );
  }

  return (
    <section className="w-full flex flex-col justify-start items-end p-6 gap-6 opacity-0 animate-[showtitle_1s_forwards]">
      <h1 className="w-full max-sm:text-xl text-white font-bold uppercase text-start  text-4xl">
        {t(titleKey)}
      </h1>

      {modalMessage && (
        <Modal
          title={modalMessage.title}
          text={modalMessage.text}
          ok={modalMessage.ok}
          actions={modalMessage.actions}
          onClose={() => {
            if (modalMessage.onClose) modalMessage.onClose();
            setModalMessage(null);
          }}
        />
      )}

      {vacancies.length === 0 ? (
        <p className="text-white text-lg">{t("no_vacancies")}</p>
      ) : (
        <div className="w-full flex flex-wrap justify-start gap-8">
          {vacancies.map((vacancy) => {
            const isLiked = likedVacancies.includes(Number(vacancy.id));
            return (
              <article
                key={vacancy.id}
                className="w-full max-w-[440px] pt-3 overflow-hidden flex flex-col relative justify-between gap-3 items-center h-auto rounded-lg shadow-2xl shadow-black cursor-pointer bg-white"
              >
                {/* Шапка: жестко flex-row, но с адаптивными отступами */}
                <div className="w-full px-3 flex flex-row justify-start items-start gap-3 sm:gap-5">
                  {/* Логотип: на мобилках 64px, на больших экранах 90px. shrink-0 не дает ему превратиться в овал */}
                  <img
                    className="w-[64px] h-[64px] sm:w-[90px] sm:h-[90px] rounded-xl border-[3px] bg-[#bdbdbd] border-white object-contain shadow-md shrink-0"
                    src={
                      vacancy.companyLogo
                        ? `/api/uploads/${vacancy.companyLogo}`
                        : "/api/default-company-logo.png"
                    }
                    alt={vacancy.companyName}
                  />

                  {/* Инфо-блок: min-w-0 дает текстовым тегам внутри понять, где край экрана */}
                  <ul className="flex flex-col gap-1 items-start justify-center min-w-0 pr-6">
                    <li className="w-full">
                      <Link
                        to={`/vacancy/${vacancy.id}`}
                        className="text-base sm:text-lg text-[#006674] font-semibold hover:underline block break-words leading-tight"
                      >
                        {vacancy.workname}
                      </Link>
                    </li>
                    <li className="text-xs sm:text-sm text-gray-500 break-words w-full">
                      {vacancy.companyName}
                    </li>
                    <li className="flex gap-1.5 justify-start items-start text-gray-700 text-xs sm:text-sm w-full mt-0.5">
                      <FontAwesomeIcon
                        icon={faLocationDot}
                        className="shrink-0 mt-0.5 text-gray-400"
                      />
                      <span className="break-words line-clamp-2">
                        {vacancy.workplace || t("not_specified")}
                      </span>
                    </li>
                    <li className="flex gap-1.5 justify-start items-start text-gray-700 text-xs sm:text-sm w-full">
                      <FontAwesomeIcon
                        icon={faMoneyBill1}
                        className="shrink-0 mt-0.5 text-gray-400"
                      />
                      <span className="break-words font-medium">
                        {vacancy.salary
                          ? `${vacancy.salary} ${vacancy.currency || ""}`
                          : t("contract")}
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Кнопка Лайка: на мобилках аккуратно в верхнем правом углу, чтобы не мешать тексту снизу */}
                <button
                  onClick={() => handleLike(vacancy.id)}
                  className={`absolute right-2 top-2 rounded-full border-[2px] border-white shadow-md transition-all duration-200 w-[32px] h-[32px] flex items-center justify-center z-10 text-xs ${
                    isLiked
                      ? "bg-red-500 text-white"
                      : "bg-gray-300 text-white hover:bg-[#006674]"
                  } ${user?.role === "company" ? "pointer-events-none opacity-60" : ""}`}
                >
                  <FontAwesomeIcon icon={faHeart} />
                </button>

                {/* Нижняя панель */}
                <div className="w-full mt-1 p-2 px-4 flex justify-between items-center bg-[#006674] text-white text-xs sm:text-sm">
                  <span className="flex items-center opacity-90">
                    <FontAwesomeIcon className="mr-1.5" icon={faCalendarAlt} />
                    {new Date(vacancy.created_at).toLocaleDateString()}
                  </span>
                  <Link
                    to={`/vacancy/${vacancy.id}`}
                    className="hover:underline font-semibold"
                  >
                    {t("seemore")}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="flex gap-3 mt-6">
          <button
            onClick={() => page > 1 && fetchVacancies(page - 1)}
            disabled={page === 1}
            className={`px-4 py-2 rounded-lg ${
              page === 1 ? "bg-gray-400" : "bg-[#006674] hover:bg-[#005c5c]"
            } text-white transition`}
          >
            {t("prev")}
          </button>
          <span className="text-white font-semibold">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => page < totalPages && fetchVacancies(page + 1)}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded-lg ${
              page === totalPages
                ? "bg-gray-400"
                : "bg-[#006674] hover:bg-[#005c5c]"
            } text-white transition`}
          >
            {t("next")}
          </button>
        </div>
      )}
      <Link
        className="mr-6 text-white relative before:top-full before:w-0 before:transition-all
        before:duration-200 before:h-[2px] before:absolute before:bg-white hover:before:w-full mt-4"
        to="/search?category=Ամենաբարձր+վարձատրությամբ+աշխատատեղեր"
      >
        {t("seemore")}
      </Link>
    </section>
  );
};

export default VacancyList;
